<template>
  <div class="theme-preview border shadow-sm" :style="rootStyle">
    <div v-if="header.announcement_enabled" class="tp-topbar" :style="{ background: theme.topbar_bg, color: theme.topbar_text }">
      {{ header.announcement_text || 'Announcement text' }}
    </div>
    <div class="tp-header" :style="{ background: theme.header_bg, color: theme.header_text }"
         :class="{ 'tp-header-centered': layout.header_style === 'centered', 'tp-header-compact': layout.header_style === 'compact' }">
      <strong class="tp-logo">{{ storeName }}</strong>
      <div class="tp-search">{{ header.search_placeholder || 'Search...' }}</div>
      <div class="tp-header-icons">
        <span v-if="header.show_delivery_address" class="tp-deliver"><i class="bi bi-geo-alt"></i> Deliver to</span>
        <i class="bi bi-person"></i>
        <i class="bi bi-cart3"></i>
      </div>
    </div>
    <div v-if="layout.show_category_nav" class="tp-nav" :style="{ background: theme.nav_bg, color: theme.nav_text }">
      <span><i class="bi bi-list"></i> All categories</span><span>Fruits</span><span>Dairy</span><span>Bakery</span><span>Drinks</span>
    </div>
    <div class="tp-body" :style="{ background: theme.page_bg }">
      <div class="tp-main">
        <div v-if="layout.show_category_sidebar" class="tp-sidebar" :style="cardStyle">
          <div class="tp-sidebar-title" :style="{ color: theme.heading_color }">Categories</div>
          <div v-for="n in 5" :key="n" class="tp-line"></div>
        </div>
        <div class="tp-content">
          <div class="tp-hero" :style="{ background: `linear-gradient(120deg, ${theme.primary_color}, ${theme.secondary_color})` }">
            <div>
              <div class="tp-hero-title">Big weekend deals</div>
              <span class="tp-btn" :style="{ background: theme.button_bg, color: theme.button_text }">Shop now</span>
            </div>
          </div>
          <div class="tp-heading" :style="{ color: theme.heading_color }">
            New arrivals <span :style="{ color: theme.primary_color }">View all ›</span>
          </div>
          <div class="tp-products">
            <div v-for="n in 4" :key="n" class="tp-card" :class="'tp-card-' + layout.product_card_style" :style="cardStyle">
              <div class="tp-img"><span v-if="n === 1" class="tp-badge" :style="{ background: theme.sale_badge_bg }">Low stock</span></div>
              <div class="tp-line short"></div>
              <div class="tp-price" :style="{ color: theme.price_color }">KES {{ 120 * n }}</div>
              <div class="tp-add" :style="{ background: theme.button_bg, color: theme.button_text }">Add to cart</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="tp-footer" :style="{ background: theme.footer_bg, color: theme.footer_text }">
      <span>{{ footer.copyright_text || `© ${new Date().getFullYear()} ${storeName}` }}</span>
      <span><i class="bi bi-facebook me-1"></i><i class="bi bi-instagram"></i></span>
    </div>
  </div>
</template>

<script>
const RADIUS = { none: '0px', sm: '4px', md: '8px', lg: '12px', xl: '18px' }

export default {
  name: "ThemePreview",

  props: {
    settings: { type: Object, required: true },
    storeName: { type: String, default: 'Your Store' },
  },

  computed: {
    theme() { return this.settings.theme || {} },
    layout() { return this.settings.layout || {} },
    header() { return this.settings.header || {} },
    footer() { return this.settings.footer || {} },
    radius() { return RADIUS[this.theme.border_radius] || RADIUS.md },
    rootStyle() {
      return { fontFamily: `'${this.theme.font_family}', sans-serif`, '--tp-radius': this.radius }
    },
    cardStyle() {
      return { background: this.theme.card_bg, borderRadius: this.radius }
    },
  },
}
</script>

<style scoped>
.theme-preview {
  border-radius: 10px;
  overflow: hidden;
  font-size: 11px;
  user-select: none;
}

.tp-topbar {
  text-align: center;
  padding: 3px;
  font-size: 10px;
}

.tp-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
}

.tp-header-compact {
  padding: 5px 12px;
}

.tp-header-centered {
  flex-wrap: wrap;
  justify-content: center;
}

.tp-header-centered .tp-logo {
  width: 100%;
  text-align: center;
}

.tp-logo {
  font-size: 13px;
  white-space: nowrap;
}

.tp-search {
  flex: 1;
  background: #fff;
  color: #9ca3af;
  border-radius: var(--tp-radius);
  padding: 4px 8px;
  min-width: 80px;
}

.tp-header-icons {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
}

.tp-deliver {
  font-size: 10px;
  opacity: .9;
}

.tp-nav {
  display: flex;
  gap: 14px;
  padding: 5px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, .06);
}

.tp-body {
  padding: 10px;
}

.tp-main {
  display: flex;
  gap: 10px;
}

.tp-sidebar {
  width: 22%;
  padding: 8px;
}

.tp-sidebar-title {
  font-weight: 700;
  margin-bottom: 6px;
}

.tp-line {
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  margin-bottom: 6px;
}

.tp-line.short {
  width: 70%;
  margin: 6px 0 4px;
}

.tp-content {
  flex: 1;
  min-width: 0;
}

.tp-hero {
  aspect-ratio: 16 / 5;
  border-radius: var(--tp-radius);
  display: flex;
  align-items: center;
  padding: 0 16px;
  color: #fff;
}

.tp-hero-title {
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 6px;
}

.tp-btn {
  display: inline-block;
  padding: 3px 10px;
  border-radius: var(--tp-radius);
}

.tp-heading {
  display: flex;
  justify-content: space-between;
  font-weight: 700;
  margin: 10px 0 6px;
}

.tp-products {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.tp-card {
  padding: 6px;
}

.tp-card-bordered {
  border: 1px solid #e5e7eb;
}

.tp-card-elevated {
  box-shadow: 0 4px 10px rgba(0, 0, 0, .08);
}

.tp-card-minimal {
  background: transparent !important;
}

.tp-img {
  position: relative;
  aspect-ratio: 1;
  background: #eef0f3;
  border-radius: var(--tp-radius);
}

.tp-badge {
  position: absolute;
  top: 4px;
  left: 4px;
  color: #fff;
  font-size: 8px;
  padding: 1px 4px;
  border-radius: 3px;
}

.tp-price {
  font-weight: 700;
}

.tp-add {
  margin-top: 4px;
  text-align: center;
  border-radius: var(--tp-radius);
  padding: 2px;
  font-size: 10px;
}

.tp-footer {
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
}
</style>
